import moment from 'moment';
import { useEffect, useMemo } from 'react';
import { Location } from '../../../../../../base/components/Location';
import { Modal } from '../../../../../../base/components/Modal';
import { Rate } from '../../../../../../base/components/Rate';
import { Loading } from '../../../../../../base/enums';
import { useAppDispatch, useAppSelector } from '../../../../../../base/hooks';
import { timestampWithNoMillisecondsToDate } from '../../../../../../base/utils';
import { __t } from '../../../../../../base/utils/t';
import { WorkerData as WorkerDataProps } from '../../../api/interfaces';
import { responsesActions } from '../../../redux/slices/responsesSlice';
import { ContactData } from '../ContactData';
import { InviteWorkerButton } from '../InviteWorkerButton';
import { StatusSelect } from '../StatusSelect';
import { WorkerData } from '../WorkerData';
import { Icon } from '../../../../../../base/components/Icon';
import { MutualIcon } from '../../MutualIcon';

import './styles.scss';

interface Props {
    visibility: boolean;
    workerData: WorkerDataProps;
    isQuickResponse: boolean;
    closeModal: () => void;
    markNewResponseAsReadHandler: () => void;
}

export const ResponseHistoryModal = ({
    visibility,
    workerData,
    isQuickResponse,
    closeModal,
    markNewResponseAsReadHandler,
}: Props) => {
    const dispatch = useAppDispatch();

    const currencies = useAppSelector((state) => state.employerApp.config.currency);
    const responseHistory = useAppSelector((state) => state.employerResponses.responseHistory);
    const isDataLoading = useAppSelector((state) => state.app.loading === Loading.ResponseHistory);

    useEffect(() => {
        dispatch(responsesActions.getResponseHistoryByWorkerId(workerData.id));
    }, [workerData.id]);

    useEffect(() => {
        return () => {
            dispatch(responsesActions.setResponseHistory([]));
        };
    }, []);

    const getDate = (workerApplied: number | null, employerInvited: number | null) => {
        if (workerApplied && !employerInvited) {
            return moment(timestampWithNoMillisecondsToDate(workerApplied)).local().format('DD MMM YYYY HH:mm');
        }
        if (!workerApplied && employerInvited) {
            return moment(timestampWithNoMillisecondsToDate(employerInvited)).local().format('DD MMM YYYY HH:mm');
        }
        const latestActionDate = Math.max(workerApplied as number, employerInvited as number);
        if (latestActionDate) {
            return moment(timestampWithNoMillisecondsToDate(latestActionDate)).local().format('DD MMM YYYY HH:mm');
        }

        return '';
    };

    const getIcon = (workerApplied: number | null, employerInvited: number | null) => {
        if (workerApplied && !employerInvited) {
            return <Icon icon='arrow-down-regular' color='success' />;
        }
        if (!workerApplied && employerInvited) {
            return <Icon icon='arrow-up-regular' color='highlight' />;
        }

        if (workerApplied && employerInvited) {
            return <MutualIcon isActive={true} />;
        }

        return '';
    };

    const content = useMemo(() => {
        if (isDataLoading) {
            return (
                <div className='response-history-modal__loading-wrap'>
                    <div className='response-history-modal__loading-icon'></div>
                </div>
            );
        }

        if (!responseHistory.length) {
            return null;
        }

        return responseHistory.map((item) => (
            <div className='response-history-modal__reponse-item response-history-item' key={item.id}>
                <div className='response-history-item__flex-wrap'>
                    <div
                        className={`response-history-item-vacancy-data response-history-item__vacancy-data ${
                            !item.vacancy.isActive ? 'response-history-item-vacancy-data--inactive' : ''
                        }`}
                    >
                        <div className='response-history-item__date-id'>
                            {getDate(item.workerApplied, item.employerInvited)}&nbsp;·&nbsp;{__t('ID')}:{item.id}
                        </div>
                        <div className='response-history-item-vacancy-data__name-icon-wrap'>
                            {getIcon(item.workerApplied, item.employerInvited)}
                            <a
                                href={item.vacancy.url}
                                target='_blank'
                                className='response-history-item-vacancy-data__name'
                                rel='noreferrer'
                                onClick={markNewResponseAsReadHandler}
                            >
                                {item.vacancy.name}
                            </a>
                            {!item.vacancy.isActive && (
                                <div className='response-history-item-vacancy-data__archive-label'>{__t('Архив')}</div>
                            )}
                        </div>
                        <div className='response-history-item-vacancy-data__location-rate-wrap'>
                            <Location
                                flag={item.vacancy.city.country?.flag || ''}
                                city={item.vacancy.city.name}
                                className='response-history-item-vacancy-data__location'
                            />
                            <Rate
                                rateFrom={item.vacancy.rateFrom}
                                rateTo={item.vacancy.rateTo}
                                currencyId={item.vacancy.currencyId}
                                rateType={item.vacancy.rateType}
                                currencies={currencies}
                            />
                        </div>
                    </div>

                    <div className='response-history-item__interactions'>
                        {!item.employerInvited ? (
                            <InviteWorkerButton
                                isVacancyActive={item.vacancy.isActive}
                                vacancyId={item.vacancy.id}
                                workerId={workerData.id}
                                markNewResponseAsReadHandler={markNewResponseAsReadHandler}
                            />
                        ) : (
                            <StatusSelect
                                responseId={item.id}
                                hireStatus={item.hireStatus}
                                markNewResponseAsReadHandler={markNewResponseAsReadHandler}
                            />
                        )}
                    </div>
                </div>
            </div>
        ));
    }, [responseHistory, isDataLoading]);

    return (
        <Modal
            visible={visibility}
            visibilityHandler={closeModal}
            containerClassName='response-history-modal'
            className='response-history-modal__component'
        >
            <h3 className='response-history-modal__title'>{__t('Історія відгуків')}</h3>
            <div className='response-history-modal__user-data'>
                <WorkerData
                    workerData={{ ...workerData }}
                    isQuickResponse={isQuickResponse}
                    markNewResponseAsReadHandler={markNewResponseAsReadHandler}
                />
                <ContactData
                    showContacts={workerData.showContacts}
                    contacts={workerData.contacts}
                    messengerUrl={workerData.messengerUrl}
                    isUserDeleted={workerData.isDeleted}
                    markNewResponseAsReadHandler={markNewResponseAsReadHandler}
                />
            </div>
            <div className='response-history-modal__content'>{content}</div>
        </Modal>
    );
};
